<script setup lang="ts">
import { computed } from "vue";
import type { DeliveryState } from "../composables/messageDelivery";
import { t } from "../i18n";
import { vTooltip } from "../directives/tooltip";

const props = defineProps<{ status: DeliveryState }>();
const label = computed(() => t(`delivery.${props.status}`));
</script>

<template>
  <span
    class="message-status"
    :class="`message-status--${status}`"
    :data-delivery-state="status"
    role="img"
    :aria-label="label"
    v-tooltip="label"
  >
    <!-- Clock and check paths captured from the rendered WhatsApp Web UI, 2026-09-24. -->
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="16"
      :height="status === 'pending' ? 11 : 16"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <template v-if="status === 'pending'">
        <path
          d="M20.74 10.85q0-1.7-.22-2.68v-.01a6 6 0 0 0-.62-1.79 5.4 5.4 0 0 0-2.28-2.26 6 6 0 0 0-1.8-.63q-.97-.22-2.67-.22h-2.3q-1.7 0-2.68.22h-.01q-.99.2-1.79.62A5.4 5.4 0 0 0 4.1 6.38q-.42.8-.64 1.78-.2.98-.2 2.69v2.29q0 1.7.2 2.69.21.98.64 1.79a5.4 5.4 0 0 0 2.27 2.27q.7.39 1.55.58l.24.05h.01q.98.22 2.68.22h2.3q1.7 0 2.66-.22h.01a6 6 0 0 0 1.8-.62q1.48-.8 2.28-2.29.43-.8.62-1.78v-.02q.22-.98.22-2.67zm2.5 2.29q0 1.84-.27 3.17a9 9 0 0 1-.87 2.48 8 8 0 0 1-3.3 3.3v.01q-1.16.6-2.47.87-1.33.28-3.18.27h-2.3q-1.85 0-3.18-.27a9 9 0 0 1-2.48-.87 8 8 0 0 1-3.3-3.31q-.6-1.15-.87-2.46v-.02Q.75 15 .75 13.14v-2.29Q.75 9 1 7.67q.28-1.33.89-2.48a8 8 0 0 1 3.3-3.3q1.14-.6 2.47-.87a16 16 0 0 1 3.18-.26h2.3q1.84 0 3.16.27 1.32.26 2.48.87 2 1.06 3.16 3.02l.15.27q.62 1.16.87 2.48.28 1.34.27 3.18z"
        />
        <path
          d="M10.88 7.58a1.25 1.25 0 1 1 2.5 0v4.44h2.44a1.25 1.25 0 0 1 0 2.5h-3.7c-.68 0-1.24-.56-1.24-1.25z"
        />
      </template>
      <path
        v-else-if="status === 'sent'"
        d="M16.14 5.86a1 1 0 0 0-1.41.15l-7.99 9.86-3.25-3.29a.99.99 0 0 0-1.41 0 .99.99 0 0 0 0 1.41l4.03 4.09c.19.19.45.3.71.3h.05a1 1 0 0 0 .73-.37l8.7-10.73a1 1 0 0 0-.15-1.41l-.01-.01Z"
      />
      <!-- Failure uses a local exclamation drawing; it was not observed in the live reference. -->
      <path
        v-else-if="status === 'failed'"
        fill-rule="evenodd"
        d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-1 5a1 1 0 0 1 2 0v6a1 1 0 0 1-2 0V7Zm1 8.25a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5Z"
      />
      <path
        v-else
        d="M14.73 6.01a1 1 0 0 1 1.41-.15l.01.01a1 1 0 0 1 .15 1.41L7.6 18.01a1 1 0 0 1-.73.37h-.05c-.26 0-.52-.11-.71-.3l-4.03-4.09a.99.99 0 0 1 0-1.41.99.99 0 0 1 1.41 0l3.25 3.29 7.99-9.86Zm5.71.12a1 1 0 0 1 1.41-.15h-.01a1 1 0 0 1 .15 1.41l-8.41 10.45a1 1 0 0 1-.73.37h-.05a1 1 0 0 1-.71-.3l-1.36-1.26a.55.55 0 0 1-.02-.81l.56-.68c.21-.2.53-.21.75-.03l.71.58 7.71-9.58Z"
      />
    </svg>
  </span>
</template>

<style scoped>
.message-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  vertical-align: -3px;
  color: var(--wa-message-status);
}
.message-status--read {
  color: var(--wa-message-read);
}
.message-status--failed {
  color: var(--wa-message-failed);
}
</style>
